/**
 * Manages paged swatches for product cards.
 * Dynamically calculates how many swatches fit based on container width
 * and groups them into pages with prev/next navigation.
 *
 * @export
 * @class ProductSwatches
 */
export default class ProductSwatches {
  /**
   * Creates an instance of ProductSwatches.
   * @param {HTMLElement} container - Element with [data-product-item-swatches]
   */
  constructor(container) {
    this.container = container;
    this.prevBtn = container?.querySelector('[data-swatches-prev]');
    this.nextBtn = container?.querySelector('[data-swatches-next]');
    this.pagesEl = container?.querySelector('[data-swatches-pages]');
    this.resizeObserver = null;

    if (!this.prevBtn || !this.nextBtn || !this.pagesEl) return;

    this._bindEvents();
    this._init();
    this._observeResize();
  }

  _flattenPages() {
    const pageGroups = this.pagesEl.querySelectorAll('[data-swatches-page-group]');
    if (pageGroups.length === 0) return;

    const items = [];
    pageGroups.forEach(group => {
      items.push(...group.querySelectorAll('[data-swatch-item]'));
    });
    pageGroups.forEach(g => g.remove());
    items.forEach(item => this.pagesEl.appendChild(item));
  }

  _rebuildPages() {
    const items = this.pagesEl.querySelectorAll('[data-swatch-item]');
    const count = items.length;
    if (count === 0) return;

    this._flattenPages();

    const containerWidth = this.pagesEl.clientWidth;
    const firstItem = this.pagesEl.querySelector('[data-swatch-item]');
    if (!firstItem) return;

    if (containerWidth < 10) {
      requestAnimationFrame(() => this._rebuildPages());
      return;
    }

    const itemRect = firstItem.getBoundingClientRect();
    const itemWidth = itemRect.width;
    const gap = 6;
    const safetyBuffer = 8;
    const perPage = Math.max(1, Math.floor((containerWidth - safetyBuffer + gap) / (itemWidth + gap)));
    const totalPages = Math.ceil(count / perPage);

    this.container.dataset.swatchesTotalPages = String(totalPages);
    this.container.dataset.swatchesPage = '1';

    if (totalPages <= 1) {
      this.prevBtn.hidden = true;
      this.nextBtn.hidden = true;
      return;
    }

    const allItems = [...this.pagesEl.querySelectorAll('[data-swatch-item]')];
    this.pagesEl.innerHTML = '';

    for (let p = 0; p < totalPages; p++) {
      const pageDiv = document.createElement('div');
      pageDiv.className = 'swatch__page flex gap-1.5';
      pageDiv.dataset.swatchesPageGroup = 'true';
      pageDiv.dataset.swatchesPageNum = String(p + 1);
      if (p > 0) pageDiv.hidden = true;

      const start = p * perPage;
      const end = Math.min(start + perPage, allItems.length);
      for (let i = start; i < end; i++) {
        pageDiv.appendChild(allItems[i]);
      }
      this.pagesEl.appendChild(pageDiv);
    }

    this.prevBtn.hidden = true;
    this.nextBtn.hidden = totalPages <= 1;
  }

  _updateVisibility() {
    const currentPage = parseInt(this.container.dataset.swatchesPage || '1', 10);
    const totalPages = parseInt(this.container.dataset.swatchesTotalPages || '1', 10);
    const pageGroups = this.pagesEl.querySelectorAll('[data-swatches-page-group]');

    pageGroups.forEach(group => {
      const pageNum = parseInt(group.dataset.swatchesPageNum || '1', 10);
      group.hidden = pageNum !== currentPage;
    });

    this.prevBtn.hidden = currentPage <= 1;
    this.nextBtn.hidden = currentPage >= totalPages;
  }

  _goPrev() {
    const current = parseInt(this.container.dataset.swatchesPage || '1', 10);
    if (current > 1) {
      this.container.dataset.swatchesPage = String(current - 1);
      this._updateVisibility();
    }
  }

  _goNext() {
    const current = parseInt(this.container.dataset.swatchesPage || '1', 10);
    const totalPages = parseInt(this.container.dataset.swatchesTotalPages || '1', 10);
    if (current < totalPages) {
      this.container.dataset.swatchesPage = String(current + 1);
      this._updateVisibility();
    }
  }

  _bindEvents() {
    this.prevBtn.addEventListener('click', () => this._goPrev());
    this.nextBtn.addEventListener('click', () => this._goNext());
  }

  _init() {
    const items = this.pagesEl.querySelectorAll('[data-swatch-item]');
    if (items.length === 0) return;

    requestAnimationFrame(() => {
      this._rebuildPages();
      this._updateVisibility();
    });
  }

  _observeResize() {
    this.resizeObserver = new ResizeObserver(() => {
      this._rebuildPages();
      this._updateVisibility();
    });
    this.resizeObserver.observe(this.pagesEl);
  }

  /**
   * Clean up observers and listeners.
   */
  destroy() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.container = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.pagesEl = null;
  }
}
